import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Collapse,
  IconButton,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';

interface SearchPattern {
  pattern: string;
  description: string;
  keyword_examples: string[];
}

interface IntentCategory {
  name: string;
  definition: string;
  search_patterns: SearchPattern[];
  user_mindset: string;
}

interface SemanticAnalysisData {
  intent_categories: IntentCategory[];
  analysis_summary: string;
}

interface SemanticAnalysisVisualizationProps {
  data: SemanticAnalysisData;
}

const SemanticAnalysisVisualization: React.FC<SemanticAnalysisVisualizationProps> = ({ data }) => {
  // Track which categories are expanded (all collapsed by default)
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set()
  );

  const toggleCategory = (index: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Intent Categories
          </Typography>
          <Chip 
            label={`${data.intent_categories.length} Intent ${data.intent_categories.length !== 1 ? 'Categories' : 'Category'}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ 
              fontWeight: 600
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          The analysis identified the following user intent categories based on semantic patterns in your queries.
        </Typography>
      </Box>

      {/* Analysis Summary */}
      {data.analysis_summary && (
        <Alert 
          icon={<PsychologyIcon />}
          severity="info" 
          sx={{ mb: 3 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Analysis Summary
          </Typography>
          <Typography variant="body2">
            {data.analysis_summary}
          </Typography>
        </Alert>
      )}

      {/* Intent Categories Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {data.intent_categories.map((category, categoryIndex) => {
          const isExpanded = expandedCategories.has(categoryIndex);
          
          return (
            <Card key={categoryIndex}>
              {/* Clickable Category Header */}
              <Box
                onClick={() => toggleCategory(categoryIndex)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {/* Expand/Collapse Icon */}
                <IconButton
                  size="small"
                  sx={{ 
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                
                {/* Category Title */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    flex: 1,
                  }}
                >
                  {category.name}
                </Typography>
                
                {/* Search Patterns Count Chip */}
                <Chip 
                  label={`${category.search_patterns.length} pattern${category.search_patterns.length !== 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ 
                    fontWeight: 500
                  }}
                />
              </Box>

              {/* Divider with padding */}
              {isExpanded && (
                <Box 
                  sx={{ 
                    mx: 2, 
                    borderBottom: '2px solid',
                    borderColor: 'primary.main',
                  }} 
                />
              )}

              {/* Collapsible Category Content */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <CardContent sx={{ pt: 3 }}>
                  {/* Category Definition */}
                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600,
                        mb: 1,
                        color: 'text.primary'
                      }}
                    >
                      Definition
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.definition}
                    </Typography>
                  </Box>

                  {/* User Mindset */}
                  <Box sx={{ mb: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600,
                        mb: 1,
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <PsychologyIcon fontSize="small" />
                      User Mindset
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{category.user_mindset}"
                    </Typography>
                  </Box>

                  {/* Search Patterns */}
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      mb: 2,
                      color: 'text.primary'
                    }}
                  >
                    Search Patterns
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {category.search_patterns.map((pattern, patternIndex) => (
                      <Box 
                        key={patternIndex}
                        sx={{
                          pl: 2,
                          borderLeft: '4px solid',
                          borderColor: 'primary.light',
                        }}
                      >
                        {/* Pattern Name */}
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 600,
                            mb: 0.5,
                            color: 'text.primary'
                          }}
                        >
                          {pattern.pattern}
                        </Typography>

                        {/* Pattern Description */}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 1.5,
                            color: 'text.secondary'
                          }}
                        >
                          {pattern.description}
                        </Typography>

                        {/* Keywords Label */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            mb: 1,
                            color: 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                          Example Keywords:
                        </Typography>

                        {/* Keyword Examples Chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {pattern.keyword_examples.map((keyword, keywordIndex) => {
                            const isLongText = keyword.length > 50;
                            const chip = (
                              <Chip
                                label={keyword}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.8rem',
                                  height: '24px',
                                  maxWidth: '480px',
                                  borderColor: 'divider',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                  },
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                }}
                              />
                            );
                            
                            return isLongText ? (
                              <Tooltip key={keywordIndex} title={keyword} arrow>
                                {chip}
                              </Tooltip>
                            ) : (
                              <Box key={keywordIndex}>{chip}</Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default SemanticAnalysisVisualization;

